import { QueryClientProvider } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import type { Session, SessionManager } from '../auth/session';
import type { GroupChatTransport } from '../chat/group-chat';
import type { AppConfig } from '../config/environment';
import type { GroupDashboardSource } from '../domain/group-dashboard';
import { type FlagProvider, type Flags, resolveFlags } from '../flags/flags';
import { type Analytics, noopAnalytics } from '../observability/analytics';
import { createCachedQueryClient } from '../query/persist';
import { forgetUnsavedExerciseMinutes } from './exercise-minutes-store';

interface EchoContextValue {
  config: AppConfig;
  sessionManager: SessionManager;
  session: Session;
  analytics: Analytics;
  flags: Flags;
  groupChatTransport: GroupChatTransport | undefined;
  groupDashboardSource: GroupDashboardSource | undefined;
}

const EchoContext = createContext<EchoContextValue | undefined>(undefined);

export interface EchoProviderProps {
  config: AppConfig;
  sessionManager: SessionManager;
  analytics?: Analytics;
  flagProvider?: FlagProvider;
  /** Production requires server-enforced membership; local previews may inject an in-memory mock. */
  groupChatTransport?: GroupChatTransport;
  /** Dashboard summaries for the caller's existing groups; membership stays owned by Core. */
  groupDashboardSource?: GroupDashboardSource;
  children: ReactNode;
}

/**
 * The one provider both apps mount at their root. Owns the QueryClient, the
 * session subscription, analytics and flags — everything platform-agnostic.
 * Platform concerns (fonts, safe areas, navigation) wrap it, never live in it.
 */
export function EchoProvider({
  config,
  sessionManager,
  analytics = noopAnalytics,
  flagProvider,
  groupChatTransport,
  groupDashboardSource,
  children,
}: EchoProviderProps) {
  const [cache] = useState(() =>
    createCachedQueryClient(
      `${config.appVersion}|${config.buildHash}`,
      sessionManager.getSession(),
    ),
  );
  const queryClient = cache.client;
  const [session, setSession] = useState<Session>(() => sessionManager.getSession());

  useEffect(() => cache.start(), [cache]);
  // The cache checks the account before the new session reaches any screen.
  useEffect(
    () =>
      sessionManager.subscribe((next) => {
        cache.observe(next);
        setSession(next);
      }),
    [sessionManager, cache],
  );
  useEffect(() => {
    void sessionManager.hydrate();
  }, [sessionManager]);
  // A session that ends takes its data with it. Nothing fetched for one person may be shown
  // to — or decide for, as `useGlobalGroup` would — the next person who signs in on this device.
  useEffect(() => {
    if (session.status === 'anonymous' || session.status === 'expired') {
      queryClient.clear();
      forgetUnsavedExerciseMinutes();
    }
  }, [session.status, queryClient]);

  const flags = useMemo(() => resolveFlags(flagProvider), [flagProvider]);
  const value = useMemo<EchoContextValue>(
    () => ({
      config,
      sessionManager,
      session,
      analytics,
      flags,
      groupChatTransport,
      groupDashboardSource,
    }),
    [config, sessionManager, session, analytics, flags, groupChatTransport, groupDashboardSource],
  );

  return (
    <EchoContext.Provider value={value}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </EchoContext.Provider>
  );
}

function useEcho(): EchoContextValue {
  const context = useContext(EchoContext);
  if (!context) throw new Error('useEcho must be used inside <EchoProvider>');
  return context;
}

export const useConfig = (): AppConfig => useEcho().config;
export const useSession = (): Session => useEcho().session;
export const useSessionManager = (): SessionManager => useEcho().sessionManager;
export const useAnalytics = (): Analytics => useEcho().analytics;
export const useFlags = (): Flags => useEcho().flags;
export const useGroupChatTransport = (): GroupChatTransport | undefined =>
  useEcho().groupChatTransport;
export const useGroupDashboardSource = (): GroupDashboardSource | undefined =>
  useEcho().groupDashboardSource;
